import { DEMO_MODE } from "../demo/flag.js";
import { demoRequest } from "../demo/demoBackend.js";

const BASE_URL = "";

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Simulated build: serve from the in-browser model, never hit the network.
  if (DEMO_MODE) return demoRequest<T>(method, path, body);

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error ?? `Request failed: ${res.status}`);
  }

  return res.json();
}

export const api = {
  world: {
    get: () => request<WorldInfo>("GET", "/api/world"),
    stats: () => request<WorldStats>("GET", "/api/world/stats"),
  },
  topologies: {
    list: () => request<TopologyInfo[]>("GET", "/api/topologies"),
    get: (id: string) => request<TopologyInfo>("GET", `/api/topologies/${id}`),
    create: (body: any) => request<{ id: string }>("POST", "/api/topologies", body),
    remove: (id: string) => request<void>("DELETE", `/api/topologies/${id}`),
    pause: (id: string) => request<void>("POST", `/api/topologies/${id}/pause`),
    resume: (id: string) => request<void>("POST", `/api/topologies/${id}/resume`),
  },
  runs: {
    list: (topologyId?: string) =>
      request<RunInfo[]>("GET", `/api/runs${topologyId ? `?topologyId=${topologyId}` : ""}`),
    get: (id: string) => request<RunInfo>("GET", `/api/runs/${id}`),
    start: (body: any) => request<{ runId: string }>("POST", "/api/runs", body),
    cancel: (id: string) => request<void>("POST", `/api/runs/${id}/cancel`),
    pause: (id: string) => request<void>("POST", `/api/runs/${id}/pause`),
    resume: (id: string) => request<void>("POST", `/api/runs/${id}/resume`),
    input: (id: string, body: any) =>
      request<void>("POST", `/api/runs/${id}/input`, body),
  },
  agents: {
    list: (topologyId?: string) =>
      request<AgentInfo[]>("GET", `/api/agents${topologyId ? `?topologyId=${topologyId}` : ""}`),
  },
  context: {
    list: (topologyId: string, prefix?: string) =>
      request<any[]>("GET", `/api/context/${topologyId}${prefix ? `?prefix=${prefix}` : ""}`),
    entries: (topologyId: string, kinds?: string[]) =>
      request<any[]>("GET", `/api/context/${topologyId}/entries${kinds ? `?kinds=${kinds.join(",")}` : ""}`),
  },
  feedback: {
    list: (runId?: string, limit = 100) =>
      request<any[]>("GET", `/api/feedback${runId ? `?runId=${runId}&` : "?"}limit=${limit}`),
  },
};

// ── Types ──────────────────────────────────────────────────

export interface WorldInfo {
  status: string;
  topologyCount: number;
  runCount: number;
  activeRunCount: number;
  topologies: Array<{
    id: string;
    type: string;
    status: string;
    runCount: number;
  }>;
}

export interface WorldStats {
  status: string;
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  totalCostUsd: number;
  totalTokens: { input: number; output: number };
}

export interface TopologyInfo {
  id: string;
  definition: any;
  status: string;
  persistentAgentIds: string[];
  runCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface RunInfo {
  id: string;
  topologyInstanceId: string;
  status: string;
  input: string;
  output?: string;
  supervised: boolean;
  totalCostUsd: number;
  totalTokens: { input: number; output: number; cached: number };
  pendingInputs: PendingInput[];
  entryAgent?: string;
  participatingAgents?: string[];
  startedAt: number;
  completedAt?: number;
}

export interface PendingInput {
  feedbackMessageId: string;
  runId: string;
  question: string;
  inputType: string;
  options?: Array<{ value: string; label: string }>;
  timeoutMs?: number;
  createdAt: number;
}

export interface AgentInfo {
  agentId: string;
  topologyInstanceId: string;
  name: string;
  status: string;
  costUsd: number;
  tokenUsage: { input: number; output: number; cached: number };
  messageCount: number;
  lastActiveAt: number;
}
